import DownloadIcon from "@mui/icons-material/Download"
import {
  Alert,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material"
import {
  DataGrid,
  type GridColDef,
  type GridColumnGroupingModel,
} from "@mui/x-data-grid"
import { useMemo, useState } from "react"
import { DateRangeFilter } from "../components/DateRangeFilter"
import {
  GroupFilterSelect,
  useGroupFilter,
} from "../components/GroupFilterSelect"
import { ReportPageHeader } from "../components/ReportPageHeader"
import { userMandatedTrainingReportPage } from "../constants/reportPages"
import {
  exportUserMandatedTrainingAsCSV,
  GetUserMandatedTrainingParams,
  getPaginationMeta,
  MandatedTrainingReportInfo,
  useGetUserMandatedTrainingQuery,
} from "../services/api"
import { useAppSelector } from "../store/hooks"
import { selectActiveOrgId } from "../store/sessionSlice"

type UserMandatedTrainingDateFilters = Partial<
  Pick<GetUserMandatedTrainingParams, "start_date" | "end_date">
>

function formatDate(date: string | null | undefined) {
  if (!date) return "-"
  return new Date(`${date}T00:00:00`).toLocaleDateString()
}

function getMandatedTrainingKey(training: MandatedTrainingReportInfo) {
  return String(training.id ?? training.uuid ?? training.title ?? "unknown")
}

function UserMandatedTrainingPage() {
  const activeOrgId = useAppSelector(selectActiveOrgId)
  const { selectedGroups, setSelectedGroups, groupParams } =
    useGroupFilter(activeOrgId)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })
  const [validationError, setValidationError] = useState<string | null>(null)
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
  })
  const [appliedFilters, setAppliedFilters] =
    useState<UserMandatedTrainingDateFilters>({})

  const queryParams = {
    ...appliedFilters,
    ...groupParams,
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    org_id: activeOrgId,
  }

  const { data, error, isFetching } =
    useGetUserMandatedTrainingQuery(queryParams)
  const paginationMeta = getPaginationMeta(data)

  const { columns, columnGroupingModel } = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: "userName",
        headerName: "User Name",
        width: 220,
        sortable: false,
        valueGetter: (_value, row) => {
          const firstName = row.user?.first_name || ""
          const lastName = row.user?.last_name || ""
          return `${firstName} ${lastName}`.trim() || "-"
        },
      },
      {
        field: "userEmail",
        headerName: "Email",
        width: 260,
        sortable: false,
        valueGetter: (_value, row) => row.user?.email || "-",
      },
    ]

    const mandatedTrainingDefinitions = Array.from(
      new Map(
        (data?.data || [])
          .flatMap((row) => row.mandated_trainings || [])
          .map((training) => [getMandatedTrainingKey(training), training])
      ).values()
    )

    const mandatedTrainingColumns: GridColDef[] =
      mandatedTrainingDefinitions.flatMap((training) => {
        const trainingKey = getMandatedTrainingKey(training)
        const getRowTraining = (row: {
          mandated_trainings?: MandatedTrainingReportInfo[]
        }) =>
          (row.mandated_trainings || []).find(
            (candidate) => getMandatedTrainingKey(candidate) === trainingKey
          )

        return [
          {
            field: `mandatedTraining:${trainingKey}:deadline`,
            headerName: "Deadline",
            width: 150,
            sortable: false,
            valueGetter: (_value, row) => {
              const rowTraining = getRowTraining(row)
              return formatDate(rowTraining?.deadline_date)
            },
          },
          {
            field: `mandatedTraining:${trainingKey}:completed`,
            headerName: "Completed Date",
            width: 160,
            sortable: false,
            valueGetter: (_value, row) => {
              const rowTraining = getRowTraining(row)
              return formatDate(rowTraining?.in_compliance_date)
            },
          },
        ]
      })

    const columnGroupingModel: GridColumnGroupingModel =
      mandatedTrainingDefinitions.map((training) => {
        const trainingKey = getMandatedTrainingKey(training)

        return {
          groupId: `mandatedTraining:${trainingKey}`,
          headerName: training.title || "Untitled Mandated Training",
          children: [
            { field: `mandatedTraining:${trainingKey}:deadline` },
            { field: `mandatedTraining:${trainingKey}:completed` },
          ],
        }
      })

    return {
      columns: [...baseColumns, ...mandatedTrainingColumns],
      columnGroupingModel,
    }
  }, [data?.data])

  const handleFilterChange = (
    field: "start_date" | "end_date",
    value: string
  ) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  const handleApplyFilters = () => {
    const nextFilters: UserMandatedTrainingDateFilters = {}
    const startMs = filters.start_date ? Date.parse(filters.start_date) : null
    const endMs = filters.end_date ? Date.parse(filters.end_date) : null

    if (startMs !== null && Number.isNaN(startMs)) {
      setValidationError("Start date is invalid.")
      return
    }

    if (endMs !== null && Number.isNaN(endMs)) {
      setValidationError("End date is invalid.")
      return
    }

    if (startMs !== null && endMs !== null && startMs > endMs) {
      setValidationError("Start date must be on or before end date.")
      return
    }

    if (filters.start_date) {
      nextFilters.start_date = filters.start_date
    }

    if (filters.end_date) {
      nextFilters.end_date = filters.end_date
    }

    setValidationError(null)
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    setAppliedFilters(nextFilters)
  }

  const handleClearFilters = () => {
    setSelectedGroups([])
    setValidationError(null)
    setPaginationModel({ page: 0, pageSize: 25 })
    setFilters({
      start_date: "",
      end_date: "",
    })
    setAppliedFilters({})
  }

  const handleExportCSV = () => {
    exportUserMandatedTrainingAsCSV({
      ...appliedFilters,
      ...groupParams,
      org_id: activeOrgId,
    })
  }

  return (
    <Stack spacing={4}>
      <ReportPageHeader
        title={userMandatedTrainingReportPage.pageTitle}
        description={userMandatedTrainingReportPage.endpointDescription}
        action={
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
            disabled={isFetching}
          >
            Export CSV
          </Button>
        }
      />

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            alignItems="flex-end"
          >
            <GroupFilterSelect
              organizationId={activeOrgId}
              value={selectedGroups}
              onChange={(value) => {
                setSelectedGroups(value)
                setPaginationModel((prev) => ({ ...prev, page: 0 }))
              }}
            />
            <DateRangeFilter
              label="Deadline Date Range"
              startDate={filters.start_date}
              endDate={filters.end_date}
              onStartDateChange={(value) =>
                handleFilterChange("start_date", value)
              }
              onEndDateChange={(value) => handleFilterChange("end_date", value)}
            />
            <Button variant="contained" onClick={handleApplyFilters}>
              Apply
            </Button>
            <Button variant="outlined" onClick={handleClearFilters}>
              Clear
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {validationError && <Alert severity="warning">{validationError}</Alert>}

      {error && (
        <Alert severity="error">
          Error loading mandated training data. Please try again.
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={data?.data || []}
          getRowId={(row) =>
            row.user?.id ?? row.user?.uuid ?? row.user?.email ?? "unknown-user"
          }
          columns={columns}
          columnGroupingModel={columnGroupingModel}
          columnGroupHeaderHeight={48}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={paginationMeta?.total || paginationMeta?.count || 0}
          paginationMode="server"
          loading={isFetching}
          pageSizeOptions={[10, 25, 50, 100]}
          disableColumnSorting
          disableRowSelectionOnClick
          autoHeight
        />
      </Card>
    </Stack>
  )
}

export default UserMandatedTrainingPage
